import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, ViewEncapsulation } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'rxjs';
import { ListItem } from 'src/app/angular-core/components/basic-list/basic-list.component';
import { CompanyType, Company } from 'src/app/generated/CrossPoint.IdentityManagement.Server';
import { getDealerCompaniesAction, getInstallerCompaniesAction, getRetailerCompaniesAction } from 'src/app/store/actions/identitymanagement.actions';
import { selectDealerCompanies, selectInstallerCompanies, selectRetailerCompanies } from 'src/app/store/selectors/identitymanagment.selector';
import { COMPANY_TYPE_ICONS } from 'src/app/utils/constants/company.consts';
import { IRemoteXPACPortalState } from 'src/app/utils/interfaces/store.interfaces';

export interface CompanyFilter {
  header?: string;
  filteredCompaniesTitle?: string;
  selectedCompany?: Company;
  excludedCompanies?: Company[];
  companyType?: CompanyType;
  companyTypes?: CompanyType[];
  includeNoneOption?: boolean;
  subCompaniesOnly?: boolean;
}

@UntilDestroy()
@Component({
  selector: 'xpac-company-filter',
  templateUrl: './company-filter.component.html',
  styleUrls: ['./company-filter.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None
})
export class CompanyFilterComponent implements OnInit {
  private static readonly DEFAULT_NR_OF_COMPANIES_TO_RETRIEVE: number = 100;
  private static readonly SEARCH_DEBOUNCE_TIME_MS: number = 500;

  private companyTypes: CompanyType[] = null;

  public header: string = "";
  public filteredCompaniesTitle: string = "";
  public selectedCompany: Company;
  public searchText: string = "";
  public isLoadingFilteredCompanies: boolean = true;
  public companies: ListItem<Company>[];
  
  private excludedCompanies: Company[] = [];
  private searchTimeoutHandle: number | null = null;
  private originalCompany: Company;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: CompanyFilter,
    public dialogRef: MatDialogRef<CompanyFilterComponent>,
    private store: Store<IRemoteXPACPortalState>,
    private cdr: ChangeDetectorRef,
    private translate: TranslateService,
    ) {

    if (data) {
      this.header = data.header;
      this.filteredCompaniesTitle = data.filteredCompaniesTitle;
      this.selectedCompany = data.selectedCompany;
      this.originalCompany = this.selectedCompany;
      this.companyTypes = data.companyTypes ?? [data.companyType];
      if (!this.companyTypes) {
        throw new Error('Provide either companyType or companyTypes');
      }
      this.excludedCompanies = data.excludedCompanies;
    }
  }

  public onSelectedValueChanged(): void {
    if (this.selectedCompany !== this.originalCompany) {
      this.onBackClick();
    }
  }

  public ngOnInit(): void {
    this.initListeners();
  }

  public async onSearchTextChanged(searchText: string): Promise<void> {
    this.searchText = searchText;

    if (this.searchTimeoutHandle !== null) {
      clearTimeout(this.searchTimeoutHandle);
    }

    while (this.isLoadingFilteredCompanies) {
      // Wait a little so an previous call can be dispatched first.
      await new Promise(resolve => setTimeout(resolve, 100));
    }

    this.isLoadingFilteredCompanies = true;
    this.cdr.markForCheck();

    // Wait some time before requesting
    this.searchTimeoutHandle = window.setTimeout(() => {
      this.retrieveCompanies(searchText);
    }, CompanyFilterComponent.SEARCH_DEBOUNCE_TIME_MS);
  }

  public getDescription(company: Company): string {
    return company.description !== "" ? company.description : company.companyId;
  }

  private getNoneCompany(type: CompanyType): Company {
    return {
      companyId: null,
      companyType: type,
      code: '',
      shardContainerId: '',
      description: this.translate.instant('Modules.Licenses.List.NoCompanyName'),
    };
  }

  private retrieveCompanies(searchText: string): void {
    this.companies = [];
    const subCompaniesOnly = this.data.subCompaniesOnly ?? false;
    for (const companyType of this.companyTypes) {
      switch (companyType) {
        case CompanyType.Dealer: {
          this.store.dispatch(getDealerCompaniesAction({
            data: {
              search: searchText,
              maxItems: CompanyFilterComponent.DEFAULT_NR_OF_COMPANIES_TO_RETRIEVE, subCompaniesOnly
            }
          }));
          break;
        }
        case CompanyType.Installer: {
          this.store.dispatch(getInstallerCompaniesAction({
            data: {
              search: searchText,
              maxItems: CompanyFilterComponent.DEFAULT_NR_OF_COMPANIES_TO_RETRIEVE, subCompaniesOnly
            }
          }));
          break;
        }
        case CompanyType.Retailer: {
          this.store.dispatch(getRetailerCompaniesAction({
            data: {
              search: searchText,
              maxItems: CompanyFilterComponent.DEFAULT_NR_OF_COMPANIES_TO_RETRIEVE, subCompaniesOnly
            }
          }));
          break;
        }
        default: {
          break;
        }
      }
    }
  }

  private initListeners(): void {
    // First get the companies
    this.retrieveCompanies("");

    this.store.select(selectDealerCompanies)
      .pipe(
        filter<Company[]>(Boolean),
        untilDestroyed(this)
      )
      .subscribe((companies: Company[]) => {
        this.onCompaniesUpdated(companies, CompanyType.Dealer);
      });

    this.store.select(selectInstallerCompanies)
      .pipe(
        filter<Company[]>(Boolean),
        untilDestroyed(this)
      )
      .subscribe((companies: Company[]) => {
        this.onCompaniesUpdated(companies, CompanyType.Installer);
      });

    this.store.select(selectRetailerCompanies)
      .pipe(
        filter<Company[]>(Boolean),
        untilDestroyed(this)
      )
      .subscribe((companies: Company[]) => {
        this.onCompaniesUpdated(companies, CompanyType.Retailer);
      });
  }

  private onCompaniesUpdated(companies: Company[], type: CompanyType): void {
    if (!this.companyTypes.includes(type)) {
      return;
    }

    if (this.data.includeNoneOption) {
      companies = [this.getNoneCompany(this.data.companyType), ...companies];
    }

    const companyItems: ListItem<Company>[] = companies.map(c => ({
      name: c.description?.length > 0 ? c.description : c.companyId,
      data: c,
      icon: COMPANY_TYPE_ICONS[c.companyType],
    }));

    this.companies = [...this.companies, ...companyItems];
    // Remove duplicates and excluded companies
    this.companies = this.companies.reduce((acc: ListItem<Company>[], cur: ListItem<Company>) => {
      if (!acc.find(c => c.data.companyId === cur.data.companyId) && !this.excludedCompanies?.find(c => c.companyId === cur.data.companyId)) {
        acc.push(cur);
      }
      return acc;
    }, []);
    this.companies.sort((a: ListItem<Company>, b: ListItem<Company>) => {
      // Make sure the 'none' company is always first
      if (a.data.companyId === null) {
        return -1;
      }
      else if (b.data.companyId === null) {
        return 1;
      }

      // Case-insensitive ordering of the company names
      return a.name.localeCompare(b.name, undefined, { sensitivity: 'base' });
    });

    this.isLoadingFilteredCompanies = false;
    this.cdr.markForCheck();
  }

  public onBackClick(): void {
    const returnValue: CompanyFilter = { selectedCompany: this.selectedCompany, companyType: this.selectedCompany?.companyType ?? this.companyTypes[0] }
    this.dialogRef.close(returnValue);
  }
}
